<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="close"
    >
      <div class="bg-white rounded-2xl shadow-2xl w-full h-full max-w-[98vw] max-h-[95vh] overflow-hidden">
        <!-- Header -->
        <div class="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <div>
                <h2 class="text-2xl font-bold">RVS Analyzer</h2>
                <p class="text-red-100 text-sm">Analýza logů tepelných čerpadel</p>
              </div>
            </div>
            <button
              @click="close"
              class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-4 overflow-y-auto flex-1" style="max-height: calc(95vh - 120px);">
          <!-- Upload Area -->
          <div
            v-if="!isAnalyzing && !reportHtml"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            :class="[
              'border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer',
              isDragging
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 hover:border-red-400 hover:bg-gray-50',
            ]"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept=".csv"
              @change="handleFileSelect"
              class="hidden"
            />

            <div class="space-y-4">
              <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>

              <div>
                <p class="text-lg font-semibold text-gray-700">
                  Přetáhněte CSV soubory sem
                </p>
                <p class="text-sm text-gray-500 mt-1">
                  nebo klikněte pro výběr souborů
                </p>
              </div>

              <div class="text-xs text-gray-400">
                <p>Podporované formáty: CSV logy z RVS21/RVS61</p>
                <p>Maximální velikost: 50 MB</p>
              </div>
            </div>
          </div>

          <!-- Selected Files -->
          <div v-if="selectedFiles.length > 0 && !isAnalyzing && !reportHtml" class="mt-6 space-y-3">
            <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Vybrané soubory ({{ selectedFiles.length }})
            </h3>

            <div class="space-y-2">
              <div
                v-for="(file, index) in selectedFiles"
                :key="index"
                class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p class="text-sm font-medium text-gray-700">{{ file.name }}</p>
                    <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
                  </div>
                </div>
                <button
                  @click="removeFile(index)"
                  class="text-red-600 hover:bg-red-50 rounded-full p-1 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <button
              @click="startAnalysis"
              class="w-full mt-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Spustit analýzu
            </button>
          </div>

          <!-- Analyzing State -->
          <div v-if="isAnalyzing" class="text-center py-12">
            <div class="inline-flex items-center justify-center space-x-2 mb-4">
              <div class="w-4 h-4 bg-red-600 rounded-full animate-bounce"></div>
              <div class="w-4 h-4 bg-orange-600 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
              <div class="w-4 h-4 bg-red-600 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
            </div>

            <h3 class="text-xl font-bold text-gray-800 mb-2">Analyzuji data...</h3>
            <p class="text-gray-600 mb-6">{{ analysisStatus }}</p>

            <div class="max-w-md mx-auto bg-gray-100 rounded-lg p-4">
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-gradient-to-r from-red-600 to-orange-600 h-2 rounded-full transition-all duration-500"
                  :style="{ width: analysisProgress + '%' }"
                ></div>
              </div>
              <p class="text-sm text-gray-600 mt-2">{{ analysisProgress }}%</p>
            </div>
          </div>

          <!-- Report Viewer -->
          <div v-if="reportHtml && !isAnalyzing" class="space-y-4">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Analýza dokončena
              </h3>
              <button
                @click="reset"
                class="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Nová analýza
              </button>
            </div>

            <div class="border rounded-xl overflow-hidden">
              <iframe
                :srcdoc="reportHtml"
                class="w-full"
                style="height: calc(95vh - 250px); min-height: 500px;"
                sandbox="allow-scripts allow-same-origin allow-modals allow-popups allow-forms"
              ></iframe>
            </div>

            <div class="flex gap-3">
              <button
                @click="downloadReport"
                class="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Stáhnout report
              </button>
              <button
                @click="openReportInNewTab"
                class="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Otevřít v novém okně
              </button>
            </div>
          </div>

          <!-- Error State -->
          <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <div class="flex items-start gap-3">
              <svg class="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 class="font-semibold text-red-800">Chyba při analýze</h4>
                <p class="text-sm text-red-600 mt-1">{{ error }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'analysis-complete']);

// State
const selectedFiles = ref([]);
const isDragging = ref(false);
const isAnalyzing = ref(false);
const analysisProgress = ref(0);
const analysisStatus = ref('Načítám soubory...');
const reportHtml = ref(null);
const error = ref(null);
const fileInput = ref(null);

// Methods
const close = () => {
  emit('close');
};

const reset = () => {
  selectedFiles.value = [];
  isAnalyzing.value = false;
  analysisProgress.value = 0;
  analysisStatus.value = 'Načítám soubory...';
  reportHtml.value = null;
  error.value = null;
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files);
  addFiles(files);
};

const handleDrop = (event) => {
  isDragging.value = false;
  const files = Array.from(event.dataTransfer.files).filter(file =>
    file.name.endsWith('.csv')
  );
  addFiles(files);
};

const addFiles = (files) => {
  const csvFiles = files.filter(file => file.name.endsWith('.csv'));
  selectedFiles.value = [...selectedFiles.value, ...csvFiles];
  error.value = null;
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const startAnalysis = async () => {
  if (selectedFiles.value.length === 0) {
    error.value = 'Vyberte alespoň jeden CSV soubor';
    return;
  }

  isAnalyzing.value = true;
  error.value = null;
  analysisProgress.value = 10;
  analysisStatus.value = 'Nahrávám soubory...';

  try {
    const formData = new FormData();
    selectedFiles.value.forEach(file => {
      formData.append('file', file);
    });

    analysisProgress.value = 30;
    analysisStatus.value = 'Köhler už se hrabe v datech';

    const response = await fetch('/api/rvs-analyzer/analyze', {
      method: 'POST',
      body: formData,
    });

    analysisProgress.value = 70;
    analysisStatus.value = 'Generuji report...';

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Analýza selhala');
    }

    const html = await response.text();

    analysisProgress.value = 100;
    analysisStatus.value = 'Hotovo!';

    setTimeout(() => {
      reportHtml.value = html;
      isAnalyzing.value = false;
      emit('analysis-complete', html);
    }, 500);

  } catch (err) {
    console.error('Analysis error:', err);
    error.value = err.message || 'Nepodařilo se dokončit analýzu';
    isAnalyzing.value = false;
    analysisProgress.value = 0;
  }
};

const downloadReport = () => {
  if (!reportHtml.value) return;

  const blob = new Blob([reportHtml.value], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rvs-analysis-${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const openReportInNewTab = () => {
  if (!reportHtml.value) return;

  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    alert('Popup blocker zablokoval otevření nového okna. Prosím povol popup okna pro tuto stránku.');
    return;
  }

  newWindow.document.open();
  newWindow.document.write(reportHtml.value);
  newWindow.document.close();
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9);
}
</style>
